"use client";

import { useMemo, useSyncExternalStore } from "react";
import { useParams, usePathname, useRouter } from "next/navigation";
import { routerQueryService } from "@/src/shared/services/routerQuery";
import { locationSearch } from "@/src/shared/utils/locationSearch";
import { announceNavigation } from "@/src/shared/utils/navigationHistory";

/** The router components use: `next/navigation` with a `query` and `asPath`. */
export interface IAppRouter {
  /** The path's params and the search params together, as the pages router's `query`. */
  query: Record<string, string | string[] | undefined>;
  /** The real path, `/listing/12`. */
  pathname: string;
  /** The path with its search, `/listing/12?from=…`. */
  asPath: string;
  /** False until the search is known: on the server, while hydrating (as the pages
   *  router's `isReady` on a static page), and for a navigation's first render, before
   *  the address bar shows the new page. */
  isReady: boolean;
  push: (href: string, options?: { scroll?: boolean }) => void;
  replace: (href: string, options?: { scroll?: boolean }) => void;
  back: () => void;
  /** Re-renders the route's server data. */
  refresh: () => void;
}

/**
 * The router components use: `next/navigation` with the pages router's `query` and
 * `asPath`. The search comes from `locationSearch`, so a statically rendered page renders
 * on the server with an empty `query` (`isReady` false) and gets the real one after
 * hydrating, instead of rendering on the client only as `useSearchParams` would make it.
 */
export function useAppRouter(): IAppRouter {
  const navigation = useRouter();
  const pathname = usePathname() ?? "";
  const params = useParams();
  const location = useSyncExternalStore(
    locationSearch.subscribe,
    locationSearch.getSnapshot,
    locationSearch.getServerSnapshot,
  );
  // Not ready while hydrating, nor while a navigation's address bar still shows the page
  // it left (a click event would otherwise read the old page's query).
  const search = locationSearch.searchFor(location, pathname);
  const isReady = search !== null;

  const searchString = search ?? "";
  return useMemo(
    () => ({
      query: routerQueryService.merge(
        params,
        new URLSearchParams(searchString),
      ),
      pathname,
      asPath: searchString ? `${pathname}?${searchString}` : pathname,
      isReady,
      push: (href, options) => {
        // Where it started from, and the loading bar, as a followed link does.
        announceNavigation(href);
        navigation.push(href, options);
      },
      replace: (href, options) => navigation.replace(href, options),
      back: () => navigation.back(),
      refresh: () => navigation.refresh(),
    }),
    [navigation, pathname, searchString, params, isReady],
  );
}
